import {
  renderReportHeatmapBundle,
  ReportComponentOverlayMode,
  ReportLabelMode,
  ReportOverlayMode,
} from '@asset-viz/reporting/reportHeatmap'
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  message,
  Popconfirm,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd'
import {
  CommercialFdeCdfSpecificationList,
  getCdfDisplayMetadata,
  useCdfDataFetcher,
  useInitializeDerivedCdfsFromRolodexData,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoLocale } from 'features/locale'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import { PdfHeatmapWithLegend } from 'pages/reporting/reports/rug/components/PdfHeatmapWithLegend'
import {
  LAYOUT,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { useSelectableComponentCDFs } from 'pages/reporting/reports/rug/hooks/useSelectableComponentCDFs'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import {
  buildReportHeatmapBundlePaths,
  getBundleFilePaths,
  serializeReportHeatmapDescriptor,
} from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import React, { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { isInspectionThicknessCDF } from 'types'
import { associateBy } from 'utils/fde/maps'

const { Text: AntText } = Typography

type OverlayDetailSelection =
  | ReportComponentOverlayMode
  | ReportOverlayMode.None

const OVERLAY_DETAIL_OPTIONS: Array<{
  label: string
  value: OverlayDetailSelection
}> = [
  { label: 'Standard', value: ReportComponentOverlayMode.Standard },
  { label: 'Full asset geometry', value: ReportComponentOverlayMode.Full },
  { label: 'None', value: ReportOverlayMode.None },
]

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ComponentSelector = styled.div`
  margin-bottom: 1rem;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ImageCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-image {
    background-color: #ffffff;
  }
`

interface ComponentScreenshot {
  componentId: ComponentId
  componentName: string
  signedUrl: string
  heatmapPath: string
  imagePath: string
  legendSignedUrl?: string
  legendPath?: string
  descriptorPath?: string
  bundlePath: string
}

interface ComponentDrawing {
  componentId: string
  componentName: string
  signedUrl: string
  imagePath: string
  extension: string
}

const pageStyles = StyleSheet.create({
  imagePage: {
    paddingTop: SPACING.PADDING.SMALL,
    paddingBottom: SPACING.PADDING.PAGE_BOTTOM,
    paddingHorizontal: SPACING.PADDING.LARGE,
    fontFamily: TYPOGRAPHY.FONT_FAMILY,
  },
  fullPageImage: {
    width: '100%',
    height: 240,
    objectFit: 'contain',
  },
})

const PageSubheader: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignContent: 'center',
        marginBottom: SPACING.MARGIN.MEDIUM,
      }}>
      <View style={{ flexDirection: 'column' }}>
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.MEDIUM} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_MEDIUM,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.HUGE} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_EXTRA_SMALL,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
        <View
          style={{
            height: LAYOUT.HEIGHT.DIVIDER_LINE,
            margin: `${SPACING.MARGIN.SMALL} ${SPACING.MARGIN.LARGE + 2} ${SPACING.MARGIN.EXTRA_SMALL} 0`,
            width: LAYOUT.WIDTH.DECORATIVE_SMALL,
            backgroundColor: REPORT_COLORS.LIGHT_GRAY,
          }}
        />
      </View>
      <View style={{ flexDirection: 'column' }}>
        <Text
          style={{
            fontSize: TYPOGRAPHY.FONT_SIZE.LARGE,
            lineHeight: TYPOGRAPHY.LINE_HEIGHT.NORMAL,
            fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
            color: REPORT_COLORS.DARKER_GRAY,
          }}>
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{
              fontSize: TYPOGRAPHY.FONT_SIZE.MEDIUM,
              lineHeight: TYPOGRAPHY.LINE_HEIGHT.NORMAL,
              fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
              color: REPORT_COLORS.GECKO_BLUE_GREEN,
              marginTop: 2,
            }}>
            {subtitle}
          </Text>
        )}
      </View>
    </View>
  )
}

const RenderReact: FC = () => {
  const {
    selectedComponents,
    relevantCDFs,
    metadataEntryId,
    selectedComponentModels,
    assetScreenshots,
    forceRefreshAssetScreenshots,
    unit,
    cdfCache,
    componentDefaults,
    componentDatasets,
    selectedAssetModel,
    inspections,
  } = useRugReportOps()

  const [selectedComponentId, setSelectedComponentId] = useState<
    ComponentId | undefined
  >()
  const [selectedCDFId, setSelectedCDFId] = useState<string | undefined>()
  const [overlayDetail, setOverlayDetail] = useState<OverlayDetailSelection>(
    ReportComponentOverlayMode.Standard
  )
  const [loading, setLoading] = useState(false)
  const [deletingComponentId, setDeletingComponentId] =
    useState<ComponentId | null>(null)
  const [uploadingDrawing, setUploadingDrawing] = useState(false)
  const [deletingDrawingId, setDeletingDrawingId] =
    useState<ComponentId | null>(null)

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const selectedComponent = useMemo(() => {
    if (!selectedComponentId || !selectedComponents) return undefined
    return selectedComponents.find(
      (component) => component.id === selectedComponentId
    )
  }, [selectedComponentId, selectedComponents])

  const componentCDFs = useSelectableComponentCDFs({
    selectedComponent,
    relevantCDFs,
    componentDatasets,
    selectedAssetModel,
    inspections,
  })

  const relevantCDF = useMemo(() => {
    if (!selectedComponent || componentCDFs.length === 0) return undefined
    if (selectedCDFId) {
      return componentCDFs.find((cdf) => cdf.id === selectedCDFId)
    }
    return componentCDFs[0]
  }, [componentCDFs, selectedCDFId, selectedComponent])

  useEffect(() => {
    if (selectedComponent && componentCDFs.length > 0) {
      if (
        !selectedCDFId ||
        !componentCDFs.find((cdf) => cdf.id === selectedCDFId)
      ) {
        setSelectedCDFId(componentCDFs[0].id)
      }
      return
    }

    setSelectedCDFId(undefined)
  }, [componentCDFs, selectedCDFId, selectedComponent])

  const colorThreshold = useMemo(() => {
    if (!relevantCDF) return undefined

    const selectedComponentDefault = componentDefaults.find(
      (componentDefault) =>
        componentDefault.tags.component === selectedComponent?.id
    )

    return selectedComponentDefault?.data.color_scale_thresholds.get(
      relevantCDF.data.params.view.id
    )
  }, [componentDefaults, relevantCDF, selectedComponent?.id])

  const relevantCdfLegendTitle = useMemo(() => {
    if (!relevantCDF) return undefined

    return getCdfDisplayMetadata(
      relevantCDF,
      getPreferredMeasurementUnit(relevantCDF.data.params.measurement_unit)
    ).label
  }, [getPreferredMeasurementUnit, relevantCDF])

  const existingScreenshots = useMemo(() => {
    const screenshots: ComponentScreenshot[] = []

    assetScreenshots?.fullComponentHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      screenshots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
      })
    })

    return screenshots
  }, [assetScreenshots, selectedComponents])

  const existingDrawings = useMemo(() => {
    const drawings: ComponentDrawing[] = []

    assetScreenshots?.fullComponentDrawing?.forEach((drawing) => {
      if (!drawing.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === drawing.componentId
      )
      if (!component) return

      drawings.push({
        componentId: drawing.componentId,
        componentName: component.data.name,
        signedUrl: drawing.signedUrl,
        imagePath: drawing.imagePath,
        extension: drawing.extension,
      })
    })

    return drawings
  }, [assetScreenshots, selectedComponents])

  const cdfDataFetcher = useCdfDataFetcher()
  const captureComponentScreenshot = React.useCallback(async () => {
    if (!selectedComponent || !relevantCDF) {
      message.error('Unable to generate heatmap - missing required data')
      return
    }

    const selectedComponentModel = selectedComponentModels?.find(
      (componentModel) => componentModel.id === selectedComponent.id
    )

    if (!selectedComponentModel) {
      message.error('Unable to generate heatmap - missing component model')
      return
    }

    setLoading(true)
    try {
      const buffer = await cdfDataFetcher(relevantCDF)
      if (!buffer) {
        message.error('Unable to generate heatmap - missing CDF data')
        return
      }

      const includeOverlay = overlayDetail !== ReportOverlayMode.None
      const bundle = await renderReportHeatmapBundle({
        kind: 'overview',
        componentId: selectedComponent.id,
        cdfId: relevantCDF.id,
        legendTitle: relevantCdfLegendTitle,
        cdf: relevantCDF,
        componentModel: selectedComponentModel,
        bounds: selectedComponentModel.componentBounds,
        colorThreshold,
        includeAxes: true,
        includeLegend: true,
        includeOverlay,
        componentOverlayMode: includeOverlay ? overlayDetail : undefined,
        overlayMode: includeOverlay
          ? ReportOverlayMode.Embedded
          : ReportOverlayMode.None,
        labelMode: includeOverlay ? ReportLabelMode.All : ReportLabelMode.None,
        outputWidthPx: 2000,
        background: 'white',
        rawValues: buffer.values,
      })

      const paths = buildReportHeatmapBundlePaths({
        kind: 'overview',
        componentId: selectedComponent.id as ComponentId,
        descriptorHash: bundle.descriptor.hash,
      })

      const existingBundles =
        assetScreenshots?.fullComponentHeatmap?.filter(
          (screenshot) => screenshot.componentId === selectedComponent.id
        ) ?? []
      for (const existingBundle of existingBundles) {
        for (const path of getBundleFilePaths(existingBundle)) {
          await rolodexDeleteFile(metadataEntryId, path)
        }
      }

      await Promise.all([
        rolodexUploadFile(
          metadataEntryId,
          bundle.heatmapBlob,
          paths.heatmapPath
        ),
        rolodexUploadFile(metadataEntryId, bundle.legendBlob, paths.legendPath),
        rolodexUploadFile(
          metadataEntryId,
          new Blob([serializeReportHeatmapDescriptor(bundle.descriptor)], {
            type: 'application/json',
          }),
          paths.descriptorPath
        ),
      ])

      message.success('Heatmap generated successfully')
      await forceRefreshAssetScreenshots()
    } catch (error) {
      console.error('Failed to generate overview heatmap:', error)
      message.error('Failed to generate heatmap')
    } finally {
      setLoading(false)
    }
  }, [
    assetScreenshots?.fullComponentHeatmap,
    cdfDataFetcher,
    colorThreshold,
    forceRefreshAssetScreenshots,
    relevantCdfLegendTitle,
    metadataEntryId,
    overlayDetail,
    relevantCDF,
    rolodexDeleteFile,
    rolodexUploadFile,
    selectedComponent,
    selectedComponentModels,
  ])

  const handleDeleteScreenshot = React.useCallback(
    async (screenshot: ComponentScreenshot) => {
      setDeletingComponentId(screenshot.componentId as ComponentId)
      try {
        for (const path of getBundleFilePaths(screenshot)) {
          await rolodexDeleteFile(metadataEntryId, path)
        }
        message.success('Heatmap deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete heatmap bundle:', error)
        message.error('Failed to delete heatmap')
      } finally {
        setDeletingComponentId(null)
      }
    },
    [forceRefreshAssetScreenshots, metadataEntryId, rolodexDeleteFile]
  )

  const handleDrawingUpload = React.useCallback(
    async (file: File) => {
      if (!selectedComponent) {
        message.error('Please select a component first')
        return
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      const validExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp']
      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        message.error(
          'Please upload a valid image file (png, jpg, jpeg, gif, webp)'
        )
        return
      }

      setUploadingDrawing(true)
      try {
        const path = `asset-drawing/fullComponentDrawing/${selectedComponent.id}.${fileExtension}`
        await rolodexUploadFile(metadataEntryId, file, path)
        message.success('Drawing uploaded successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to upload drawing:', error)
        message.error('Failed to upload drawing')
      } finally {
        setUploadingDrawing(false)
      }
    },
    [
      forceRefreshAssetScreenshots,
      metadataEntryId,
      rolodexUploadFile,
      selectedComponent,
    ]
  )

  const handleDeleteDrawing = React.useCallback(
    async (drawing: ComponentDrawing) => {
      setDeletingDrawingId(drawing.componentId as ComponentId)
      try {
        await rolodexDeleteFile(metadataEntryId, drawing.imagePath)
        message.success('Drawing deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete drawing:', error)
        message.error('Failed to delete drawing')
      } finally {
        setDeletingDrawingId(null)
      }
    },
    [forceRefreshAssetScreenshots, metadataEntryId, rolodexDeleteFile]
  )

  const missingComponents = useMemo(() => {
    const screenshotComponentIds = new Set(
      existingScreenshots.map((screenshot) => screenshot.componentId)
    )
    return (
      selectedComponents?.filter(
        (component) => !screenshotComponentIds.has(component.id as ComponentId)
      ) || []
    )
  }, [existingScreenshots, selectedComponents])

  const componentOptions = useMemo(() => {
    return (
      selectedComponents?.map((component) => ({
        label: component.data.name,
        value: component.id,
      })) || []
    )
  }, [selectedComponents])

  useInitializeDerivedCdfsFromRolodexData(
    cdfCache,
    CommercialFdeCdfSpecificationList,
    unit?.id ?? [],
    isInspectionThicknessCDF
  )

  const cdfOptions = componentCDFs.map((cdf) => ({
    label: `${
      getCdfDisplayMetadata(
        cdf,
        getPreferredMeasurementUnit(cdf.data.params.measurement_unit)
      ).label
    }${
      cdf.data.params.aggregation.name
        ? ` (${cdf.data.params.aggregation.name})`
        : ''
    }`,
    value: cdf.id,
  }))

  return (
    <Container>
      <ComponentSelector>
        <Space orientation='vertical' style={{ width: '100%' }}>
          <AntText strong>Select Component to Capture:</AntText>
          <Select
            style={{ width: '100%' }}
            placeholder='Select a component'
            value={selectedComponentId}
            onChange={setSelectedComponentId}
            options={componentOptions}
          />
          {selectedComponent && cdfOptions.length > 0 && (
            <>
              <AntText strong>Select CDF:</AntText>
              <Select
                style={{ width: '100%' }}
                placeholder='Select a CDF'
                value={selectedCDFId}
                onChange={setSelectedCDFId}
                options={cdfOptions}
              />
              <AntText strong>Overlay Detail:</AntText>
              <Select
                style={{ width: '100%' }}
                value={overlayDetail}
                onChange={setOverlayDetail}
                options={OVERLAY_DETAIL_OPTIONS}
              />
            </>
          )}
        </Space>
      </ComponentSelector>

      {selectedComponent && (
        <Space orientation='vertical' style={{ width: '100%' }}>
          <Button
            type='primary'
            onClick={captureComponentScreenshot}
            loading={loading}
            disabled={!selectedComponent}>
            Generate Heatmap
          </Button>

          <Upload
            accept='image/png,image/jpeg,image/jpg,image/gif,image/webp'
            beforeUpload={(file) => {
              handleDrawingUpload(file)
              return false
            }}
            showUploadList={false}
            maxCount={1}
            disabled={!selectedComponent}>
            <Button loading={uploadingDrawing} disabled={!selectedComponent}>
              Upload Asset Drawing
            </Button>
          </Upload>
        </Space>
      )}

      {missingComponents.length > 0 && (
        <Alert
          message='Missing Component Screenshots'
          description={
            <div>
              <p>The following components do not have screenshots yet:</p>
              <ul>
                {missingComponents.map((component) => (
                  <li key={component.id}>{component.data.name}</li>
                ))}
              </ul>
            </div>
          }
          type='warning'
          showIcon
        />
      )}

      <PreviewSection>
        <Typography.Title level={4}>Captured Screenshots</Typography.Title>
        {existingScreenshots.map((screenshot) => (
          <ImageCard
            key={screenshot.componentId}
            title={screenshot.componentName}>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                width: '100%',
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AntImage
                  src={screenshot.signedUrl}
                  alt={screenshot.componentName}
                  style={{ display: 'block', width: '100%' }}
                />
              </div>
              {screenshot.legendSignedUrl && (
                <AntImage
                  src={screenshot.legendSignedUrl}
                  alt={`${screenshot.componentName} legend`}
                  style={{ width: '72px' }}
                />
              )}
            </div>
            <Popconfirm
              title='Delete heatmap?'
              description='This will remove the heatmap bundle from the report.'
              okText='Delete'
              cancelText='Cancel'
              okButtonProps={{
                danger: true,
                loading: deletingComponentId === screenshot.componentId,
              }}
              onConfirm={() => handleDeleteScreenshot(screenshot)}>
              <Button
                danger
                loading={deletingComponentId === screenshot.componentId}
                style={{ marginTop: '0.75rem' }}>
                Delete Screenshot
              </Button>
            </Popconfirm>
          </ImageCard>
        ))}
      </PreviewSection>

      <PreviewSection>
        <Typography.Title level={4}>Uploaded Drawings</Typography.Title>
        {existingDrawings.length === 0 ? (
          <AntText type='secondary'>No drawings uploaded yet</AntText>
        ) : (
          existingDrawings.map((drawing) => (
            <ImageCard key={drawing.componentId} title={drawing.componentName}>
              <AntImage
                src={drawing.signedUrl}
                alt={drawing.componentName}
                style={{ width: '100%', maxWidth: '600px' }}
              />
              <Popconfirm
                title='Delete drawing?'
                description='This will remove the drawing from the report.'
                okText='Delete'
                cancelText='Cancel'
                okButtonProps={{
                  danger: true,
                  loading: deletingDrawingId === drawing.componentId,
                }}
                onConfirm={() => handleDeleteDrawing(drawing)}>
                <Button
                  danger
                  loading={deletingDrawingId === drawing.componentId}
                  style={{ marginTop: '0.75rem' }}>
                  Delete Drawing
                </Button>
              </Popconfirm>
            </ImageCard>
          ))
        )}
      </PreviewSection>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData, selectedComponents, assetScreenshots } = useRugReportOps()

  const screenshots = useMemo(() => {
    const shots: ComponentScreenshot[] = []

    assetScreenshots?.fullComponentHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      shots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
      })
    })

    return shots
  }, [assetScreenshots, selectedComponents])

  const drawings = useMemo(() => {
    const drwgs: ComponentDrawing[] = []

    assetScreenshots?.fullComponentDrawing?.forEach((drawing) => {
      if (!drawing.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === drawing.componentId
      )
      if (!component) return

      drwgs.push({
        componentId: drawing.componentId,
        componentName: component.data.name,
        signedUrl: drawing.signedUrl,
        imagePath: drawing.imagePath,
        extension: drawing.extension,
      })
    })

    return drwgs
  }, [assetScreenshots, selectedComponents])

  const drawingsByComponentId = useMemo(() => {
    return associateBy({
      arr: drawings,
      key: 'componentId',
    })
  }, [drawings])

  const screenshotsByComponentId = useMemo(() => {
    return associateBy({
      arr: screenshots,
      key: 'componentId',
    })
  }, [screenshots])

  const componentsWithMedia = useMemo(() => {
    if (!selectedComponents) return []
    return selectedComponents.filter(
      (component) =>
        drawingsByComponentId.has(component.id as ComponentId) ||
        screenshotsByComponentId.has(component.id as ComponentId)
    )
  }, [drawingsByComponentId, screenshotsByComponentId, selectedComponents])

  if (componentsWithMedia.length === 0) return null

  let isFirstPage = true

  return (
    <>
      {componentsWithMedia.map((component) => {
        const drawing = drawingsByComponentId.get(component.id as ComponentId)
        const screenshot = screenshotsByComponentId.get(
          component.id as ComponentId
        )
        const pages = []

        if (drawing) {
          const tracker = isFirstPage
            ? {
                sectionId: RugReportSectionId.DATA_OVERVIEW,
                onRender,
              }
            : undefined

          pages.push(
            <ReportPage
              key={`drawing-${drawing.componentId}`}
              id={isFirstPage ? 'data-overview' : undefined}
              size='LETTER'
              orientation='landscape'
              pageStyle={pageStyles.imagePage}
              wrap={false}
              contentStyle={{ margin: 0 }}
              tracker={tracker}
              header={<PageHeader title='Data Overview' />}
              footer={
                <PageFooter
                  jobNumber={formData.mainInfo.assetId}
                  revisionNumber={formData.revisionMetadata.revisionNumber}
                />
              }>
              <ImagePageHeader
                componentId={drawing.componentId as ComponentId}
              />
              <PageSubheader
                title='Asset Drawing'
                subtitle={drawing.componentName}
              />
              <Image
                src={drawing.signedUrl}
                cache={false}
                style={pageStyles.fullPageImage}
              />
            </ReportPage>
          )
          isFirstPage = false
        }

        if (screenshot) {
          const tracker = isFirstPage
            ? {
                sectionId: RugReportSectionId.DATA_OVERVIEW,
                onRender,
              }
            : undefined

          pages.push(
            <ReportPage
              key={`screenshot-${screenshot.componentId}`}
              id={isFirstPage ? 'data-overview' : undefined}
              size='LETTER'
              orientation='landscape'
              pageStyle={pageStyles.imagePage}
              wrap={false}
              contentStyle={{ margin: 0 }}
              tracker={tracker}
              header={<PageHeader title='Data Overview' />}
              footer={
                <PageFooter
                  jobNumber={formData.mainInfo.assetId}
                  revisionNumber={formData.revisionMetadata.revisionNumber}
                />
              }>
              <ImagePageHeader
                componentId={screenshot.componentId as ComponentId}
              />
              <PageSubheader
                title='C-Scan Overview'
                subtitle={screenshot.componentName}
              />
              <PdfHeatmapWithLegend
                heatmapSrc={screenshot.signedUrl}
                legendSrc={screenshot.legendSignedUrl}
                gap={4}
              />
            </ReportPage>
          )
          isFirstPage = false
        }

        return pages
      })}
      <AddendumPages
        sectionId={RugReportSectionId.DATA_OVERVIEW}
        sectionTitle='Data Overview'
      />
    </>
  )
}

export const DataOverviewSection: ReportSection = {
  id: RugReportSectionId.DATA_OVERVIEW,
  title: 'Data Overview',
  description: 'Component thickness visualizations',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
